import { useEffect } from "react";

// Lightweight, dependency-free per-route SEO + Open Graph + Twitter card manager.
// Updates <title> and the relevant <meta> tags on mount and keeps them in sync.

function upsertMeta(selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  title,
  description,
  image,
  path = "/",
  type = "website",
}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} · Unite Recruitment`
      : "Unite Recruitment — We help candidates and employers unite.";
    const desc =
      description ||
      "A boutique New Zealand technology recruitment partner that takes the time to understand the person on both sides of the table.";
    const origin =
      typeof window !== "undefined" && window.location
        ? `${window.location.protocol}//${window.location.host}`
        : "";
    const url = `${origin}${path}`;
    const imgUrl = image ? (image.startsWith("http") ? image : `${origin}${image}`) : "";

    document.title = fullTitle;

    upsertMeta('meta[name="description"]', "name", "description", desc);

    // Open Graph
    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", desc);
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "Unite Recruitment");
    if (imgUrl) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", imgUrl);
      upsertMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
      upsertMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
    }

    // Twitter / X
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", imgUrl ? "summary_large_image" : "summary");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    if (imgUrl) {
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", imgUrl);
    }

    // Canonical
    upsertLink("canonical", url);
  }, [title, description, image, path, type]);

  return null;
}
